import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { getCurrentUserName } from 'features/auth/ducks/selectors'
import IncidentsAPI from 'services/IncidentsAPI'
import SearchableSelect from 'components/SearchableSelect'

const RIDDOR_KINDS = [
  ['death', 'The death of a person'],
  ['specified_injury', 'A specified injury to a worker (fracture, amputation, loss of sight, serious burn, etc.)'],
  ['over_7_day', 'Over-7-day injury to a worker — off normal work for more than 7 days'],
  ['non_fatal_public', 'A member of the public taken to hospital — non-fatal accident to a non-worker'],
  ['over_3_day', 'Over-3-day injury to a worker — record only'],
  ['disease', 'A reportable occupational disease'],
  ['dangerous_occurrence', 'A dangerous occurrence — a near miss with serious potential'],
  ['gas_incident', 'A dangerous gas fitting or gas incident'],
]

const MOR_TYPES = [
  ['fire_spread', 'Risk relating to the spread of fire'],
  ['fire_safety', 'Risk relating to fire safety measures'],
  ['structural_failure', 'Risk relating to structural failure'],
]

const ROOT_CAUSE_LABELS = {
  equipment_failure: 'Equipment failure',
  human_error: 'Human error',
  procedure_gap: 'Procedure gap / not followed',
  environmental: 'Environmental conditions',
  third_party: 'Third party',
  design_defect: 'Design defect',
  maintenance_lapse: 'Maintenance lapse',
  unknown: 'Not yet determined',
}

const today = () => new Date().toISOString().slice(0, 10)

const CompleteReportModal = ({ open, type, incident, onClose, onSaved }) => {
  const isMor = type === 'mor'
  const currentUserName = useSelector(getCurrentUserName)
  const saved = incident?.reportDetails?.[type] || {}

  const defaults = useMemo(() => {
    const causeLabel = incident?.rootCause ? (ROOT_CAUSE_LABELS[incident.rootCause] || '') : ''
    const morDefaultTypes = incident?.category === 'fire'
      ? ['fire_spread', 'fire_safety']
      : incident?.category === 'structural'
        ? ['structural_failure']
        : []
    return {
      description: saved.description || incident?.narrative || '',
      declarationName: saved.declarationName || currentUserName || '',
      declarationDate: saved.declarationDate || today(),
      incidentKind: saved.incidentKind || '',
      likelyCause: saved.likelyCause || causeLabel,
      injuredPersonType: saved.injuredPersonType || '',
      injuredName: saved.injuredName || '',
      injuredAgeOccupation: saved.injuredAgeOccupation || '',
      injuryNature: saved.injuryNature || '',
      bodyPart: saved.bodyPart || '',
      takenToHospital: saved.takenToHospital || false,
      noticeReference: saved.noticeReference || '',
      noticeSubmittedBy: saved.noticeSubmittedBy || '',
      noticeSubmitterContact: saved.noticeSubmitterContact || '',
      occurrenceTypes: saved.occurrenceTypes || morDefaultTypes,
      cause: saved.cause || causeLabel,
      howDiscovered: saved.howDiscovered || '',
      whoInvolved: saved.whoInvolved || '',
      immediateActions: saved.immediateActions || '',
      futureActions: saved.futureActions || '',
      lessonsLearned: saved.lessonsLearned || '',
      supportingInfo: saved.supportingInfo || '',
      investigatorName: saved.investigatorName || incident?.investigatorName || '',
    }
  }, [incident?.id, type, open])

  const [form, setForm] = useState(defaults)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (open) setForm(defaults)
  }, [open, incident?.id, type, defaults])

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }))
  const toggleType = value => setForm(prev => ({
    ...prev,
    occurrenceTypes: prev.occurrenceTypes.includes(value)
      ? prev.occurrenceTypes.filter(v => v !== value)
      : [...prev.occurrenceTypes, value],
  }))

  const valid = isMor
    ? form.occurrenceTypes.length > 0 && form.description.trim() && form.declarationName.trim() && form.declarationDate
    : form.incidentKind && form.description.trim() && form.declarationName.trim() && form.declarationDate

  const buildPayload = () => (isMor
    ? {
      noticeReference: form.noticeReference,
      noticeSubmittedBy: form.noticeSubmittedBy,
      noticeSubmitterContact: form.noticeSubmitterContact,
      occurrenceTypes: form.occurrenceTypes,
      description: form.description,
      cause: form.cause,
      howDiscovered: form.howDiscovered,
      whoInvolved: form.whoInvolved,
      immediateActions: form.immediateActions,
      futureActions: form.futureActions,
      lessonsLearned: form.lessonsLearned,
      supportingInfo: form.supportingInfo,
      investigatorName: form.investigatorName,
      declarationName: form.declarationName,
      declarationDate: form.declarationDate,
    }
    : {
      incidentKind: form.incidentKind,
      description: form.description,
      likelyCause: form.likelyCause,
      injuredPersonType: form.injuredPersonType,
      injuredName: form.injuredName,
      injuredAgeOccupation: form.injuredAgeOccupation,
      injuryNature: form.injuryNature,
      bodyPart: form.bodyPart,
      takenToHospital: form.takenToHospital,
      supportingInfo: form.supportingInfo,
      declarationName: form.declarationName,
      declarationDate: form.declarationDate,
    })

  const downloadDocx = async () => {
    const result = await IncidentsAPI.downloadReport(incident.id, type)
    const blob = new Blob([result.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
    const url = window.URL.createObjectURL(blob)
    const disposition = result.headers?.['content-disposition'] || ''
    const match = /filename="?([^"]+)"?/.exec(disposition)
    const link = document.createElement('a')
    link.href = url
    link.download = match ? match[1] : `${type}-report-${today()}.docx`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const submit = async () => {
    if (!valid || busy) return
    setBusy(true)
    try {
      await IncidentsAPI.saveReportDetails(incident.id, type, buildPayload())
      await downloadDocx()
      onSaved?.()
      onClose?.()
      toast.success(`${isMor ? 'Mandatory Occurrence Report' : 'RIDDOR record'} downloaded.`)
    } catch (e) {
      const msg = e?.response?.data?.message || 'Could not generate the report. Please check the details and try again.'
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={!!open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 17, fontWeight: 700, pb: 0.5 }}>
        {isMor ? 'Complete the Mandatory Occurrence Report' : 'Complete the RIDDOR record'}
      </DialogTitle>
      <DialogContent>
        <Intro>
          Fill in the details below. When you download, you'll get a finished Word document ready to
          submit through the {isMor ? "Building Safety Regulator's" : 'HSE'} online service — no blanks left to complete by hand.
        </Intro>

        {isMor ? (
          <>
            <SubHead>Mandatory occurrence notice</SubHead>
            <Field label="Notice reference">
              <TextInput value={form.noticeReference} onChange={e => set('noticeReference', e.target.value)} disabled={busy} placeholder="The reference from the notice you already submitted" />
            </Field>
            <TwoCol>
              <Field label="Who submitted the notice (if not you)">
                <TextInput value={form.noticeSubmittedBy} onChange={e => set('noticeSubmittedBy', e.target.value)} disabled={busy} />
              </Field>
              <Field label="Their contact details">
                <TextInput value={form.noticeSubmitterContact} onChange={e => set('noticeSubmitterContact', e.target.value)} disabled={busy} placeholder="Phone or email" />
              </Field>
            </TwoCol>

            <SubHead>The safety occurrence</SubHead>
            <FieldGroup>
              <FieldLabel>Type of safety occurrence <Req>*</Req></FieldLabel>
              <Checks>
                {MOR_TYPES.map(([value, label]) => (
                  <CheckRow key={value}>
                    <input
                      type="checkbox"
                      id={`mor-${value}`}
                      checked={form.occurrenceTypes.includes(value)}
                      onChange={() => toggleType(value)}
                      disabled={busy}
                    />
                    <label htmlFor={`mor-${value}`}>{label}</label>
                  </CheckRow>
                ))}
              </Checks>
            </FieldGroup>

            <Field label="What happened, or what could have happened?" required>
              <TextArea rows={4} value={form.description} onChange={e => set('description', e.target.value)} disabled={busy} />
            </Field>
            <Field label="What caused the occurrence?">
              <TextArea rows={2} value={form.cause} onChange={e => set('cause', e.target.value)} disabled={busy} />
            </Field>
            <Field label="How was the occurrence discovered?">
              <TextArea rows={2} value={form.howDiscovered} onChange={e => set('howDiscovered', e.target.value)} disabled={busy} />
            </Field>
            <Field label="Who was involved, and the effect or potential effect on them?">
              <TextArea rows={2} value={form.whoInvolved} onChange={e => set('whoInvolved', e.target.value)} disabled={busy} />
            </Field>
            <Field label="Immediate actions taken to keep people safe">
              <TextArea rows={2} value={form.immediateActions} onChange={e => set('immediateActions', e.target.value)} disabled={busy} />
            </Field>
            <Field label="Longer-term actions to prevent recurrence">
              <TextArea rows={2} value={form.futureActions} onChange={e => set('futureActions', e.target.value)} disabled={busy} />
            </Field>
            <Field label="Person(s) investigating the occurrence">
              <TextInput value={form.investigatorName} onChange={e => set('investigatorName', e.target.value)} disabled={busy} />
            </Field>
            <Field label="Anything others should learn from">
              <TextArea rows={2} value={form.lessonsLearned} onChange={e => set('lessonsLearned', e.target.value)} disabled={busy} />
            </Field>
            <Field label="Supporting information (documents, photos, videos)">
              <TextArea rows={2} value={form.supportingInfo} onChange={e => set('supportingInfo', e.target.value)} disabled={busy} placeholder="Note anything that supports this report. Files attached to the incident are listed automatically." />
            </Field>
          </>
        ) : (
          <>
            <Field label="Kind of reportable incident" required>
              <SearchableSelect
                value={form.incidentKind}
                onChange={v => set('incidentKind', v)}
                options={RIDDOR_KINDS.map(([value, label]) => ({ value, label }))}
                placeholder="Select the kind of incident…"
                disabled={busy}
                ariaLabel="Kind of reportable incident"
              />
            </Field>
            <Field label="Description of what happened" required>
              <TextArea rows={4} value={form.description} onChange={e => set('description', e.target.value)} disabled={busy} />
            </Field>
            <Field label="Likely cause">
              <TextInput value={form.likelyCause} onChange={e => set('likelyCause', e.target.value)} disabled={busy} />
            </Field>
            <SubHead>The injured or affected person</SubHead>
            <Sub>Leave blank if no one was injured (for example, a dangerous occurrence).</Sub>
            <Field label="Worker or member of the public?">
              <SearchableSelect
                value={form.injuredPersonType}
                onChange={v => set('injuredPersonType', v)}
                options={[
                  { value: 'worker', label: 'Worker' },
                  { value: 'public', label: 'Member of the public (non-worker)' },
                ]}
                clearable
                clearLabel="Not applicable / not known"
                placeholder="Not applicable / not known"
                disabled={busy}
                ariaLabel="Worker or member of the public?"
              />
            </Field>
            <TwoCol>
              <Field label="Full name">
                <TextInput value={form.injuredName} onChange={e => set('injuredName', e.target.value)} disabled={busy} />
              </Field>
              <Field label="Age / occupation">
                <TextInput value={form.injuredAgeOccupation} onChange={e => set('injuredAgeOccupation', e.target.value)} disabled={busy} />
              </Field>
            </TwoCol>
            <TwoCol>
              <Field label="Nature of injury or condition">
                <TextInput value={form.injuryNature} onChange={e => set('injuryNature', e.target.value)} disabled={busy} />
              </Field>
              <Field label="Part of body affected">
                <TextInput value={form.bodyPart} onChange={e => set('bodyPart', e.target.value)} disabled={busy} />
              </Field>
            </TwoCol>
            <FieldGroup>
              <CheckRow>
                <input
                  type="checkbox"
                  id="taken-to-hospital"
                  checked={!!form.takenToHospital}
                  onChange={e => set('takenToHospital', e.target.checked)}
                  disabled={busy}
                />
                <label htmlFor="taken-to-hospital">The injured person was taken to hospital for treatment</label>
              </CheckRow>
            </FieldGroup>
            <Field label="Supporting information (documents, photos, videos)">
              <TextArea rows={2} value={form.supportingInfo} onChange={e => set('supportingInfo', e.target.value)} disabled={busy} placeholder="Note anything that supports this report. Files attached to the incident are listed automatically." />
            </Field>
          </>
        )}

        <SubHead>Declaration</SubHead>
        <TwoCol>
          <Field label="Report completed by" required>
            <TextInput value={form.declarationName} onChange={e => set('declarationName', e.target.value)} disabled={busy} />
          </Field>
          <Field label="Date completed" required>
            <TextInput type="date" value={form.declarationDate} onChange={e => set('declarationDate', e.target.value)} disabled={busy} />
          </Field>
        </TwoCol>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SecondaryBtn type="button" onClick={onClose} disabled={busy}>Cancel</SecondaryBtn>
        <PrimaryBtn type="button" onClick={submit} disabled={busy || !valid}>
          {busy ? 'Preparing…' : 'Download completed Word document'}
        </PrimaryBtn>
      </DialogActions>
    </Dialog>
  )
}

const Field = ({ label, required, children }) => (
  <FieldGroup>
    <FieldLabel>{label} {required && <Req>*</Req>}</FieldLabel>
    {children}
  </FieldGroup>
)

export default CompleteReportModal

const Intro = styled.p`
  margin: 0 0 14px 0;
  font-size: 13px;
  line-height: 1.55;
  color: #475569;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 12px;
`

const FieldLabel = styled.label`
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
`

const Req = styled.span`
  color: #b53a1d;
  font-weight: 700;
`

const inputStyles = `
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  color: #0f172a;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  &:focus { outline: none; border-color: #D24724; }
  &:disabled { background: #f8fafc; color: #94a3b8; }
`

const TextInput = styled.input`${inputStyles}`
const TextArea = styled.textarea`
  ${inputStyles}
  resize: vertical;
  line-height: 1.5;
`

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`

const SubHead = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
  margin: 10px 0 4px 0;
`

const Sub = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 8px;
`

const Checks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const CheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #0f172a;
  input { width: 15px; height: 15px; accent-color: #D24724; }
  label { cursor: pointer; }
`

const Btn = styled.button`
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const SecondaryBtn = styled(Btn)`
  background: white;
  border: 1px solid #e5e7eb;
  color: #475569;
  &:hover:not(:disabled) { background: #f8fafc; }
`

const PrimaryBtn = styled(Btn)`
  background: #D24724;
  border: 1px solid #D24724;
  color: white;
  &:hover:not(:disabled) { background: #b53a1d; border-color: #b53a1d; }
`
